import React, { useCallback, useEffect, useState } from "react";
import {
	getAuthProviders,
	saveAuthProvider,
	deleteAuthProvider,
} from "../../api/apiServiceFetch";
import "./AuthProviders.scss";

// Совпадает с ProviderAuthKind на бэкенде. Telegram и телефон не проверяются
// по aud, поэтому их здесь нет.
const PROVIDERS = [
	{ value: 2, label: "Google" },
	{ value: 1, label: "Apple" },
];

const PLATFORMS = [
	{ value: "web", label: "Веб" },
	{ value: "ios", label: "iOS" },
	{ value: "android", label: "Android" },
];

const emptyRow = () => ({
	id: null,
	provider: 2,
	audience: "",
	platform: "web",
	isEnabled: true,
	note: "",
});

const providerLabel = (value) =>
	PROVIDERS.find((p) => p.value === value)?.label ?? `#${value}`;

const AuthProviders = () => {
	const [items, setItems] = useState([]);
	const [form, setForm] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [status, setStatus] = useState(null);

	const reload = useCallback(async () => {
		setIsLoading(true);
		try {
			setItems(await getAuthProviders());
		} catch (error) {
			console.error("Load auth providers error:", error);
			setStatus({ ok: false, text: "Не удалось загрузить" });
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		reload();
	}, [reload]);

	const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));

	const onSave = async () => {
		if (!form?.audience?.trim()) {
			setStatus({ ok: false, text: "Идентификатор обязателен" });
			return;
		}

		setIsSaving(true);
		setStatus(null);
		try {
			const result = await saveAuthProvider({
				id: form.id ?? undefined,
				provider: form.provider,
				audience: form.audience.trim(),
				platform: form.platform || null,
				isEnabled: !!form.isEnabled,
				note: form.note?.trim() || null,
			});

			if (result?.isSuccess === false) {
				setStatus({ ok: false, text: result.message || "Не удалось сохранить" });
				return;
			}

			setForm(null);
			await reload();
			setStatus({ ok: true, text: "Сохранено" });
		} catch (error) {
			console.error("Save auth provider error:", error);
			setStatus({ ok: false, text: "Не удалось сохранить" });
		} finally {
			setIsSaving(false);
		}
	};

	const onDelete = async (item) => {
		if (!window.confirm(`Удалить ${item.audience}?`)) return;

		try {
			await deleteAuthProvider(item.id);
			if (form?.id === item.id) setForm(null);
			await reload();
			setStatus({ ok: true, text: "Удалено" });
		} catch (error) {
			console.error("Delete auth provider error:", error);
			setStatus({ ok: false, text: "Не удалось удалить" });
		}
	};

	const onToggle = async (item) => {
		try {
			await saveAuthProvider({ ...item, isEnabled: !item.isEnabled });
			await reload();
		} catch (error) {
			console.error("Toggle auth provider error:", error);
			setStatus({ ok: false, text: "Не удалось переключить" });
		}
	};

	return (
		<div className="apr">
			<div className="apr__head">
				<h2>Входы через Google и Apple</h2>
				<button type="button" className="apr__btn" onClick={() => setForm(emptyRow())}>
					Добавить
				</button>
			</div>

			<p className="apr__hint">
				Идентификатор клиента для каждой платформы. Веб-кнопка появляется, только
				когда есть строка с платформой «Веб»: с мобильным client id браузер
				получит ошибку. Изменения применяются сразу, выкатка не нужна.
			</p>

			{isLoading ? (
				<div className="apr__empty">Загрузка…</div>
			) : (
				<table className="apr__table">
					<thead>
						<tr>
							<th>Провайдер</th>
							<th>Идентификатор</th>
							<th>Платформа</th>
							<th>Включён</th>
							<th>Заметка</th>
							<th />
						</tr>
					</thead>
					<tbody>
						{items.map((item) => (
							<tr key={item.id} className={item.isEnabled ? "" : "isOff"}>
								<td>{providerLabel(item.provider)}</td>
								<td className="apr__mono">{item.audience}</td>
								<td>{item.platform || "—"}</td>
								<td>
									<button
										type="button"
										className="apr__toggle"
										onClick={() => onToggle(item)}
									>
										{item.isEnabled ? "да" : "нет"}
									</button>
								</td>
								<td>{item.note || "—"}</td>
								<td className="apr__rowBtns">
									<button type="button" onClick={() => setForm({ ...item })}>
										Изменить
									</button>
									<button type="button" onClick={() => onDelete(item)}>
										Удалить
									</button>
								</td>
							</tr>
						))}

						{items.length === 0 && (
							<tr>
								<td colSpan={6} className="apr__empty">
									Ничего не заведено — внешние входы выключены
								</td>
							</tr>
						)}
					</tbody>
				</table>
			)}

			{form && (
				<div className="apr__form">
					<h3>{form.id ? "Изменить" : "Добавить"}</h3>

					<label>
						Провайдер
						<select
							value={form.provider}
							onChange={(e) => setField("provider", Number(e.target.value))}
						>
							{PROVIDERS.map((p) => (
								<option key={p.value} value={p.value}>
									{p.label}
								</option>
							))}
						</select>
					</label>

					<label>
						Идентификатор
						<input
							value={form.audience}
							onChange={(e) => setField("audience", e.target.value)}
							placeholder="…apps.googleusercontent.com"
						/>
					</label>

					<label>
						Платформа
						<select
							value={form.platform || ""}
							onChange={(e) => setField("platform", e.target.value)}
						>
							<option value="">не указана</option>
							{PLATFORMS.map((p) => (
								<option key={p.value} value={p.value}>
									{p.label}
								</option>
							))}
						</select>
					</label>

					<label className="apr__check">
						<input
							type="checkbox"
							checked={!!form.isEnabled}
							onChange={(e) => setField("isEnabled", e.target.checked)}
						/>
						Включён
					</label>

					<label>
						Заметка
						<input
							value={form.note || ""}
							onChange={(e) => setField("note", e.target.value)}
						/>
					</label>

					<div className="apr__formBtns">
						<button
							type="button"
							className="apr__btn apr__btn--primary"
							onClick={onSave}
							disabled={isSaving}
						>
							{isSaving ? "Сохранение…" : "Сохранить"}
						</button>
						<button type="button" className="apr__btn" onClick={() => setForm(null)}>
							Отмена
						</button>
					</div>
				</div>
			)}

			{status && (
				<p className={`apr__status ${status.ok ? "isOk" : "isFail"}`}>
					{status.text}
				</p>
			)}
		</div>
	);
};

export default AuthProviders;
